import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonInput,
  IonTextarea,
  IonModal,
  IonChip,
  IonBadge,
  IonAvatar,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonAlert,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/react';
import {
  add,
  personAdd,
  search,
  ellipsisVertical,
  create,
  trash,
  checkmark,
  close,
  people,
  mail,
  call,
  business,
  time,
  chevronBack,
  chevronBackOutline,
  chevronForwardOutline
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '../config/api';
import Loader from '../components/Loader';

const AdminMembersManagement = () => {
  const history = useHistory();
  const [members, setMembers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [organizationCode, setOrganizationCode] = useState(''); // New state for Org ID
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    pages: 0
  });

  // Form data for adding/editing members
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role_id: '4', // default to member
    department: '',
    position: '',
    member_id: '',
    password: ''
  });

  // Available roles
  const [userRoles, setUserRoles] = useState([
    { id: '2', name: 'Organization Admin' },
    { id: '3', name: 'Moderator' },
    { id: '4', name: 'Member' },
    { id: '5', name: 'Viewer' }
  ]);

  const [organizationStats, setOrganizationStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    admins: 0,
    moderators: 0
  });

  useEffect(() => {
    fetchMembers();
  }, [searchText, selectedRole, pagination.page]);

  const fetchMembers = async (refresh = false) => {
    let safetyTimeout;
    try {
      if (refresh) {
        setIsLoading(true);
        setPagination(prev => ({ ...prev, page: 1 }));
      }
      
      safetyTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 12000);

      const userId = sessionStorage.getItem('user_id');
      if (!userId) {
        history.push('/page/login');
        clearTimeout(safetyTimeout);
        return;
      }

      const params = new URLSearchParams({
        user_id: userId,
        page: refresh ? 1 : pagination.page,
        limit: pagination.limit,
        ...(searchText && { search: searchText }),
        ...(selectedRole && { role: selectedRole })
      });

      const response = await axios.get(`${API_CONFIG.MANAGE_MEMBERS}?${params}`, { timeout: 8000 });

      if (response.data.success) {
        if (refresh) {
          setMembers(response.data.members);
        } else {
          setMembers(prev => pagination.page === 1 ? response.data.members : [...prev, ...response.data.members]);
        }
        setPagination(response.data.pagination);
        setOrganizationCode(response.data.organization_code || ''); // Store the org code
        calculateStats(response.data.members);
      } else {
        setError(response.data.error || 'Failed to fetch members');
      }
    } catch (err) {
      setError('Failed to load members. Please try again.');
      console.error('Error fetching members:', err);
    } finally {
      clearTimeout(safetyTimeout);
      setIsLoading(false);
    }
  };

  const calculateStats = (membersList) => {
    const stats = {
      totalMembers: membersList.length,
      activeMembers: membersList.filter(m => m.is_active && m.user_active).length,
      admins: membersList.filter(m => m.role_id === '2').length,
      moderators: membersList.filter(m => m.role_id === '3').length
    };
    setOrganizationStats(stats);
  };

  const handleAddMember = async () => {
    try {
      if (!formData.name || !formData.email || !formData.role_id) {
        setError('Please fill in all required fields');
        return;
      }

      const userId = sessionStorage.getItem('user_id');
      const submitData = {
        ...formData,
        role_id: parseInt(formData.role_id)
      };

      const response = await axios.post(
        `${API_CONFIG.MANAGE_MEMBERS}?user_id=${userId}`,
        submitData,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.success) {
        setSuccess('Member added successfully');
        setShowAddModal(false);
        resetForm();
        fetchMembers(true);
      } else {
        setError(response.data.error || 'Failed to add member');
      }
    } catch (err) {
      setError('Failed to add member. Please try again.');
      console.error('Error adding member:', err);
    }
  };

  const handleEditMember = async () => {
    try {
      if (!selectedMember) return;

      const userId = sessionStorage.getItem('user_id');
      const updateData = {
        role_id: parseInt(formData.role_id),
        department: formData.department,
        position: formData.position,
        is_active: formData.is_active
      };

      const response = await axios.put(
        `${API_CONFIG.MANAGE_MEMBERS}?user_id=${userId}&id=${selectedMember.id}`,
        updateData,
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.success) {
        setSuccess('Member updated successfully');
        setShowEditModal(false);
        setSelectedMember(null);
        resetForm();
        fetchMembers(true);
      } else {
        setError(response.data.error || 'Failed to update member');
      }
    } catch (err) {
      setError('Failed to update member. Please try again.');
      console.error('Error updating member:', err);
    }
  };

  const handleDeleteMember = async () => {
    try {
      if (!selectedMember) return;

      const userId = sessionStorage.getItem('user_id');
      const response = await axios.delete(
        `${API_CONFIG.MANAGE_MEMBERS}?user_id=${userId}&id=${selectedMember.id}`
      );

      if (response.data.success) {
        setSuccess('Member removed successfully');
        setShowDeleteAlert(false);
        setSelectedMember(null);
        fetchMembers(true);
      } else {
        setError(response.data.error || 'Failed to remove member');
      }
    } catch (err) {
      setError('Failed to remove member. Please try again.');
      console.error('Error deleting member:', err);
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      role_id: member.role_id.toString(),
      department: member.department || '',
      position: member.position || '',
      member_id: member.member_id || '',
      is_active: member.is_active
    });
    setShowEditModal(true);
  };

  const openDeleteAlert = (member) => {
    setSelectedMember(member);
    setShowDeleteAlert(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role_id: '4',
      department: '',
      position: '',
      member_id: '',
      password: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const getRoleBadgeColor = (roleId) => {
    switch (roleId) {
      case '2': return 'danger'; // Admin
      case '3': return 'warning'; // Moderator
      case '4': return 'primary'; // Member
      case '5': return 'medium'; // Viewer
      default: return 'medium';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const loadMoreMembers = async (event) => {
    if (pagination.page < pagination.pages) {
      setPagination(prev => ({ ...prev, page: prev.page + 1 }));
    }
    await fetchMembers();
    event.target.complete();
  };

  const doRefresh = async (event) => {
    await fetchMembers(true);
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon icon={chevronBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>{sessionStorage.getItem('role_id') === '1' ? 'System Member Management' : 'Organization Members'}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              fill="solid"
              color="light"
              onClick={openAddModal}
              style={{
                borderRadius: '8px',
                fontWeight: '600',
                padding: '0 16px'
              }}
            >
              <IonIcon icon={personAdd} slot="start" />
              Add Member
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ background: 'linear-gradient(to bottom, #F8FAFC 0%, #F1F5F9 100%)' }}>
        {isLoading && <Loader message="Indexing Member Directory..." />}
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        {/* Statistics Cards */}
        <IonGrid className="ion-padding" style={{ paddingTop: '24px' }}>
          <IonRow>
            <IonCol size="6" sizeMd="3">
              <IonCard style={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                margin: 0
              }}>
                <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#4F46E5',
                    marginBottom: '4px'
                  }}>
                    {organizationStats.totalMembers}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.01em'
                  }}>
                    Total Members
                  </div>
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <IonCard style={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                margin: 0
              }}>
                <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#059669',
                    marginBottom: '4px'
                  }}>
                    {organizationStats.activeMembers}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.01em'
                  }}>
                    Active Members
                  </div>
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <IonCard style={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                margin: 0
              }}>
                <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#DC2626',
                    marginBottom: '4px'
                  }}>
                    {organizationStats.admins}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.01em'
                  }}>
                    Admins
                  </div>
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <IonCard style={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                margin: 0
              }}>
                <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#D97706',
                    marginBottom: '4px'
                  }}>
                    {organizationStats.moderators}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.01em'
                  }}>
                    Moderators
                  </div>
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="6" sizeMd="3">
              <IonCard style={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                margin: 0
              }}>
                <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#475569',
                    marginBottom: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {organizationCode || 'N/A'}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#64748B',
                    fontWeight: '600',
                    letterSpacing: '0.01em'
                  }}>
                    Organization ID
                  </div>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6 mx-4">
          <div className="flex flex-col-reverse md:flex-col gap-4">
            <div className="flex md:justify-end w-full">
              <IonSelect
                value={selectedRole}
                onIonChange={(e) => setSelectedRole(e.detail.value)}
                placeholder="All Roles"
                interface="popover"
                style={{
                  minWidth: '200px',
                  background: '#F1F5F9',
                  borderRadius: '10px',
                  fontWeight: '500',
                  color: '#475569'
                }}
              >
                <IonSelectOption value="">All Roles</IonSelectOption>
                {userRoles.map(role => (
                  <IonSelectOption key={role.id} value={role.id}>
                    {role.name}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </div>
            <div className="w-full">
              <IonSearchbar
                value={searchText}
                onIonInput={(e) => setSearchText(e.detail.value)}
                placeholder="Search members by name, email, department..."
                debounce={500}
                className="custom-searchbar"
              />
            </div>
          </div>
        </div>

        {/* Members List */}
        <div style={{ padding: '0 16px' }}>
          {members.map((member, index) => (
            <IonCard
              key={member.id}
              style={{
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFBFC 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
                marginBottom: '12px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)';
              }}
            >
              <IonCardContent style={{ padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  {/* Avatar */}
                  <div style={{ flexShrink: 0 }}>
                    {member.avatar_url ? (
                      <img
                        src={member.avatar_url}
                        alt={member.name}
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '12px',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        fontWeight: '700',
                        color: '#FFFFFF'
                      }}>
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Member Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 style={{
                      margin: '0 0 4px 0',
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1E293B'
                    }}>
                      {member.name}
                    </h2>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '14px',
                      color: '#64748B'
                    }}>
                      {member.email}
                    </p>

                    {(member.department || member.position) && (
                      <p style={{
                        margin: '0 0 12px 0',
                        fontSize: '13px',
                        color: '#94A3B8'
                      }}>
                        {member.department && <span>Department: {member.department}</span>}
                        {member.department && member.position && <span> • </span>}
                        {member.position && <span>Position: {member.position}</span>}
                      </p>
                    )}

                    {/* Badges */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                      <IonBadge
                        color={getRoleBadgeColor(member.role_id)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        {member.role_name}
                      </IonBadge>
                      {member.is_active && member.user_active ? (
                        <IonChip
                          color="success"
                          style={{
                            height: '28px',
                            borderRadius: '6px',
                            margin: 0
                          }}
                        >
                          <IonIcon icon={checkmark} style={{ fontSize: '14px' }} />
                          <IonLabel style={{ fontSize: '12px', fontWeight: '600' }}>Active</IonLabel>
                        </IonChip>
                      ) : (
                        <IonChip
                          color="medium"
                          style={{
                            height: '28px',
                            borderRadius: '6px',
                            margin: 0
                          }}
                        >
                          <IonIcon icon={close} style={{ fontSize: '14px' }} />
                          <IonLabel style={{ fontSize: '12px', fontWeight: '600' }}>Inactive</IonLabel>
                        </IonChip>
                      )}
                    </div>

                    {/* Meta Info */}
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#94A3B8'
                    }}>
                      Joined: {formatDate(member.created_at)}
                      {member.last_login_at && (
                        <span> • Last login: {formatDate(member.last_login_at)}</span>
                      )}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                    <IonButton
                      fill="clear"
                      onClick={() => openEditModal(member)}
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        height: '40px',
                        margin: 0
                      }}
                    >
                      <IonIcon icon={create} style={{ fontSize: '20px' }} />
                    </IonButton>
                    <IonButton
                      fill="clear"
                      color="danger"
                      onClick={() => openDeleteAlert(member)}
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        height: '40px',
                        margin: 0
                      }}
                    >
                      <IonIcon icon={trash} style={{ fontSize: '20px' }} />
                    </IonButton>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          ))}
        </div>

        {!isLoading && members.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '64px 24px',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            borderRadius: '16px',
            margin: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
              marginBottom: '24px'
            }}>
              <IonIcon
                icon={people}
                style={{ fontSize: '40px', color: '#4F46E5' }}
              />
            </div>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '20px',
              fontWeight: '700',
              color: '#1E293B'
            }}>
              No members found
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              color: '#64748B'
            }}>
              Start by adding your first organization member.
            </p>
            <IonButton
              onClick={openAddModal}
              style={{
                '--border-radius': '10px',
                fontWeight: '600'
              }}
            >
              <IonIcon icon={personAdd} slot="start" />
              Add Member
            </IonButton>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.pages > 1 && (
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            padding: '16px',
            margin: '16px 16px 32px'
          }}>
            <div style={{ fontSize: '14px', color: '#475569' }}>
              Page {pagination.page} of {pagination.pages}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <IonButton
                fill="outline"
                size="small"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                style={{ '--border-radius': '8px' }}
              >
                <IonIcon icon={chevronBackOutline} slot="icon-only" />
              </IonButton>

              {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                // Logic to show ranges centered on current page
                let p = i + 1;
                if (pagination.pages > 5) {
                  if (pagination.page > 3) {
                    p = pagination.page - 2 + i;
                  }
                  if (p > pagination.pages) {
                    p = pagination.pages - 4 + i;
                  }
                }

                return (
                  <IonButton
                    key={p}
                    fill={pagination.page === p ? 'solid' : 'outline'}
                    size="small"
                    onClick={() => setPagination(prev => ({ ...prev, page: p }))}
                    style={{
                      '--border-radius': '8px',
                      minWidth: '36px',
                      display: p > 0 && p <= pagination.pages ? 'inline-flex' : 'none'
                    }}
                  >
                    {p}
                  </IonButton>
                );
              })}

              <IonButton
                fill="outline"
                size="small"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                style={{ '--border-radius': '8px' }}
              >
                <IonIcon icon={chevronForwardOutline} slot="icon-only" />
              </IonButton>
            </div>
          </div>
        )}

        {/* Add Member Modal */}
        <IonModal
          isOpen={showAddModal}
          onDidDismiss={() => {
            setShowAddModal(false);
            resetForm();
          }}
        >
          <IonHeader>
            <IonToolbar color="primary">
              <IonTitle>Add New Member</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}>
                  <IonIcon icon={close} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ '--background': '#F8FAFC' }}>
            <div style={{ padding: '24px' }}>
              <IonCard style={{
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                margin: 0
              }}>
                <IonCardContent style={{ padding: '20px' }}>
                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Name *
                    </IonLabel>
                    <IonInput
                      value={formData.name}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, name: e.detail.value }))}
                      placeholder="Enter member's full name"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Email *
                    </IonLabel>
                    <IonInput
                      type="email"
                      value={formData.email}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, email: e.detail.value }))}
                      placeholder="Enter email address"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Phone
                    </IonLabel>
                    <IonInput
                      type="tel"
                      value={formData.phone}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, phone: e.detail.value }))}
                      placeholder="Enter phone number"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Role *
                    </IonLabel>
                    <IonSelect
                      value={formData.role_id}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, role_id: e.detail.value }))}
                      interface="popover"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    >
                      {userRoles.map(role => (
                        <IonSelectOption key={role.id} value={role.id}>
                          {role.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Department
                    </IonLabel>
                    <IonInput
                      value={formData.department}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, department: e.detail.value }))}
                      placeholder="Enter department"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Position
                    </IonLabel>
                    <IonInput
                      value={formData.position}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, position: e.detail.value }))}
                      placeholder="Enter position/title"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Member ID
                    </IonLabel>
                    <IonInput
                      value={formData.member_id}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, member_id: e.detail.value }))}
                      placeholder="Enter member ID (optional)"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '24px' }}>
                    <IonLabel position="stacked" style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      marginBottom: '8px'
                    }}>
                      Password
                    </IonLabel>
                    <IonInput
                      type="password"
                      value={formData.password}
                      onIonChange={(e) => setFormData(prev => ({ ...prev, password: e.detail.value }))}
                      placeholder="Leave empty for default password"
                      style={{
                        '--padding-start': '12px',
                        '--padding-end': '12px',
                        background: '#F1F5F9',
                        borderRadius: '8px',
                        marginTop: '4px'
                      }}
                    />
                  </IonItem>

                  <IonButton
                    expand="block"
                    onClick={handleAddMember}
                    style={{
                      '--border-radius': '10px',
                      fontWeight: '600',
                      height: '48px'
                    }}
                  >
                    Add Member
                  </IonButton>
                </IonCardContent>
              </IonCard>
            </div>
          </IonContent>
        </IonModal>

        {/* Edit Member Modal */}
        <IonModal isOpen={showEditModal} onDidDismiss={() => setShowEditModal(false)}>
          <IonHeader>
            <IonToolbar color="primary">
              <IonTitle>Edit Member</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowEditModal(false)}>
                  <IonIcon icon={close} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ '--background': '#F8FAFC' }}>
            {selectedMember && (
              <div style={{ padding: '24px' }}>
                <IonCard style={{
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  margin: 0
                }}>
                  <IonCardContent style={{ padding: '20px' }}>
                    <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                      <IonLabel position="stacked" style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569',
                        marginBottom: '8px'
                      }}>
                        Name
                      </IonLabel>
                      <IonInput
                        value={selectedMember.name}
                        readonly
                        style={{
                          '--padding-start': '12px',
                          '--padding-end': '12px',
                          background: '#E2E8F0',
                          borderRadius: '8px',
                          marginTop: '4px'
                        }}
                      />
                    </IonItem>

                    <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                      <IonLabel position="stacked" style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569',
                        marginBottom: '8px'
                      }}>
                        Email
                      </IonLabel>
                      <IonInput
                        value={selectedMember.email}
                        readonly
                        style={{
                          '--padding-start': '12px',
                          '--padding-end': '12px',
                          background: '#E2E8F0',
                          borderRadius: '8px',
                          marginTop: '4px'
                        }}
                      />
                    </IonItem>

                    <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                      <IonLabel position="stacked" style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569',
                        marginBottom: '8px'
                      }}>
                        Role *
                      </IonLabel>
                      <IonSelect
                        value={formData.role_id}
                        onIonChange={(e) => setFormData(prev => ({ ...prev, role_id: e.detail.value }))}
                        interface="popover"
                        style={{
                          '--padding-start': '12px',
                          '--padding-end': '12px',
                          background: '#F1F5F9',
                          borderRadius: '8px',
                          marginTop: '4px'
                        }}
                      >
                        {userRoles.map(role => (
                          <IonSelectOption key={role.id} value={role.id}>
                            {role.name}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>

                    <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '16px' }}>
                      <IonLabel position="stacked" style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569',
                        marginBottom: '8px'
                      }}>
                        Department
                      </IonLabel>
                      <IonInput
                        value={formData.department}
                        onIonChange={(e) => setFormData(prev => ({ ...prev, department: e.detail.value }))}
                        placeholder="Enter department"
                        style={{
                          '--padding-start': '12px',
                          '--padding-end': '12px',
                          background: '#F1F5F9',
                          borderRadius: '8px',
                          marginTop: '4px'
                        }}
                      />
                    </IonItem>

                    <IonItem lines="none" style={{ '--padding-start': '0', marginBottom: '24px' }}>
                      <IonLabel position="stacked" style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#475569',
                        marginBottom: '8px'
                      }}>
                        Position
                      </IonLabel>
                      <IonInput
                        value={formData.position}
                        onIonChange={(e) => setFormData(prev => ({ ...prev, position: e.detail.value }))}
                        placeholder="Enter position/title"
                        style={{
                          '--padding-start': '12px',
                          '--padding-end': '12px',
                          background: '#F1F5F9',
                          borderRadius: '8px',
                          marginTop: '4px'
                        }}
                      />
                    </IonItem>

                    <IonButton
                      expand="block"
                      onClick={handleEditMember}
                      style={{
                        '--border-radius': '10px',
                        fontWeight: '600',
                        height: '48px'
                      }}
                    >
                      Update Member
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Delete Confirmation Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Confirm Deletion"
          message={`Are you sure you want to remove ${selectedMember?.name} from the organization? This action cannot be undone.`}
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel'
            },
            {
              text: 'Delete',
              role: 'destructive',
              handler: handleDeleteMember
            }
          ]}
        />

        {/* Success/Error Toasts */}
        <IonToast
          isOpen={!!success}
          onDidDismiss={() => setSuccess('')}
          message={success}
          duration={3000}
          color="success"
        />

        <IonToast
          isOpen={!!error}
          onDidDismiss={() => setError('')}
          message={error}
          duration={4000}
          color="danger"
        />
      </IonContent>
    </IonPage >
  );
};

export default AdminMembersManagement;
